import { cancelDisbursement, getDisbursementById, processDisbursement } from "../services/disbursements";
import { useQueryClient } from "@tanstack/react-query";
import useModal from "./modalHook";
import CompleteDisbursement from "../views/partners/financier/disbursements/CompleteDisbursement";
import Disbursement from "../models/Disbursement";
import { useMemo } from "react";
import { formatCurrencyAmount, getFormattedDate, showToast } from "../utils/miscFunctions";
import { AlertCircle, CheckCircle2, Clock, EllipsisVerticalIcon } from "lucide-react";
import { ITableAction } from "../components/elements/GTable/GTable";
import PopOnMe from "../components/elements/poponme/PopOnMe";
import Loan from "../models/Loan";
import { DISBURSEMENT_STATUS } from "../resources/enums";
import useRoute from "./routesHook";
import { Link } from "react-router-dom";
import UploadDisbursementEvidence from "../views/partners/financier/disbursements/UploadDisbursementEvidence";
interface useDisbursementsProps {
    tableRowActions?: (loan: Loan) => ITableAction[];
}
interface StatusBadgeProps {
    status: DISBURSEMENT_STATUS | string;
}

function useDisbursements({ tableRowActions }: useDisbursementsProps) {
    const { ModalComponent, closeModal, updateModalStates } = useModal();
    const { getDashboardBase } = useRoute();
    const DisbursementModal = ModalComponent;
    const queryClient = useQueryClient();

    // Status badge component
    const StatusBadge = ({ status }: StatusBadgeProps) => {
        const normalizedStatus = status.toUpperCase();
        const getStatusInfo = () => {
            switch (normalizedStatus) {
                case DISBURSEMENT_STATUS.PENDING:
                    return { color: 'bg-yellow-100 text-yellow-800', icon: Clock };
                case DISBURSEMENT_STATUS.COMPLETED:
                    return { color: 'bg-green-100 text-green-800', icon: CheckCircle2 };
                case DISBURSEMENT_STATUS.FAILED:
                    return { color: 'bg-red-100 text-red-800', icon: AlertCircle };
                default:
                    return { color: 'bg-gray-100 text-gray-800', icon: Clock };
            }
        };

        const { color, icon: Icon } = getStatusInfo();

        return (
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full ${color}`}>
                <Icon className="w-3 h-3 mr-1" />
                <span className="capitalize">{status}</span>
            </span>
        );
    };

    const fetchDisbursementById = (disbursementId?: string): Promise<Disbursement> => {
        if (!disbursementId) return Promise.reject(new Error('Disbursement ID is required'));
        return new Promise<Disbursement>((resolve, reject) => {
            getDisbursementById(disbursementId)
                .then((response: any) => {
                    resolve(new Disbursement(response));
                })
                .catch((error: Error) => {
                    reject(error);
                });
        });
    };

    const columns = useMemo(
        () => [
            {
                Header: 'Disbursement Details',
                columns: [
                    {
                        Header: 'ID',
                        accessor: 'id',
                        Cell: (row: any) => {
                            return (
                                <Link to={`${getDashboardBase()}/disbursements/${row.row.original.id}`}>
                                    <span className={` ${row.row.original?.reference ? 'text-link-main hover:underline dark:text-darkBg-textLink dark:hover:text-darkBg-textLink' : ''}`}>{row.cell.value ?? '-'}</span>
                                </Link>
                            );
                        }
                    },
                    {
                        Header: 'Recipient',
                        accessor: 'recipient',
                        Cell: (row: any) => (
                            <span>{row.cell.value?.name || '-'}</span>
                        )
                    },
                    {
                        Header: 'Amount',
                        accessor: 'amountToSettle',
                        Cell: (row: any) => (
                            <span className="font-semibold">
                                {formatCurrencyAmount(row.cell.value)}
                            </span>
                        )
                    },
                    {
                        Header: 'Status',
                        accessor: 'status',
                        Cell: (row: any) => (
                            <StatusBadge status={row.cell.value} />
                        )
                    },
                    {
                        Header: 'Requested On',
                        accessor: 'createdOn',
                        Cell: (row: any) => (
                            <span>{getFormattedDate({ date: row.cell.value })}</span>
                        )
                    },
                    {
                        Header: 'Disbursed On',
                        accessor: 'paidOn',
                        Cell: (row: any) => (
                            <span>{getFormattedDate({ date: row.cell.value })}</span>
                        )
                    },
                    ...(tableRowActions ? [{
                        id: 'actions',
                        Cell: (row: any) => (
                            <div className="cursor-pointer flex justify-center">
                                <PopOnMe actions={tableRowActions?.(row.row.original) ?? []}>
                                    <EllipsisVerticalIcon className="h-5 w-5 text-gray-500 hover:text-gray-700" />
                                </PopOnMe>
                            </div>
                        )
                    }] : [])
                ]
            }
        ],
        []
    );

    const handleCancelDisbursement = async (disbursementId?: number) => {
        if (!disbursementId) return;
        try {
            const response: any = await cancelDisbursement(disbursementId);
            if (response) {
                showToast.success(response?.message || 'Disbursement cancelled successfully');
                queryClient.invalidateQueries({ queryKey: ['fetchDisbursementById'] });
            }
        } catch (error: any) {
            showToast.error(error?.description || 'Failed to cancel disbursement');
        }
    }

    const handleCompleteDisbursement = async (disbursement?: Disbursement) => {
        if (!disbursement || disbursement === null) return;
        updateModalStates({
            show: true,
            size: 'lg',
            children: (
                <CompleteDisbursement
                    onProceed={() => {
                        closeModal();
                        queryClient.invalidateQueries({ queryKey: ['fetchDisbursementById'] });
                    }}
                    onClose={closeModal}
                    disbursement={disbursement}
                />
            ),
            showCloseButton: true,
            classes: '!p-0 r-[5px]',
        });
    }

    const handleProcessDisbursement = async (disbursementId?: number) => {
        if (!disbursementId) return;
        try {
            const response: any = await processDisbursement(disbursementId);
            if (response) {
                showToast.success(response?.message || 'Disbursement processed successfully');
                queryClient.invalidateQueries({ queryKey: ['fetchDisbursementById'] });
            }
        } catch (error: any) {
            showToast.error(error?.description || 'Failed to process disbursement');
        }
    }

    const handleUploadEvidence = (disbursementId: number) => {
        updateModalStates({
            show: true,
            size: 'md',
            children: (
                <UploadDisbursementEvidence
                    onProceed={() => {
                        closeModal();
                        queryClient.invalidateQueries({ queryKey: ['fetchDisbursementById'] });
                    }}
                    onClose={closeModal}
                    disbursementId={disbursementId}
                />
            ),
            
            classes: '!p-0 r-[5px]'
        });
    };


    const handleEvidencePreview = (evidenceUrl?: string) => {
        if (!evidenceUrl) return;
        window.open(evidenceUrl, '_blank');
    }

    return {
        fetchDisbursementById,
        handleCancelDisbursement,
        handleCompleteDisbursement,
        handleProcessDisbursement,
        DisbursementModal,
        columns,
        StatusBadge,
        handleUploadEvidence,
        handleEvidencePreview
    }
}

export default useDisbursements;

